const DogCalculatorState = {
  open: false,
  openAgeDropdown: false,

  // Product popup
  productPopupOpen: false,
  productData: null,

  openProductPopup: function () {
    // Scroll just enough so the overlay is fully aligned with the viewport.
    // (On mobile Safari we can get a tiny misalignment, so we correct after render.)
    const el = this.$el;

    try {
      if (el && typeof el.scrollIntoView === 'function') {
        // Offset by 50px due to overlay styling
        el.scrollIntoView({ block: 'start', behavior: 'auto' });
        window.scrollBy(0, -50);
      } else if (el && el.getBoundingClientRect) {
        const rect = el.getBoundingClientRect();
        const currentScroll = window.pageYOffset || window.scrollY || 0;
        const top = rect.top + currentScroll - 50; // Offset by 50px
        window.scrollTo(0, Math.max(0, top));
      }
    } catch (e) {
      // no-op
    }

    // Let the browser apply the scroll position, then open the popup and
    // nudge scroll so the overlay itself sits correctly in the viewport.
    setTimeout(() => {
      this.productPopupOpen = true;

      const alignOverlay = () => {
        try {
          const overlay = el ? el.querySelector('#product-popup .overlay') : null;
          if (!overlay || !overlay.getBoundingClientRect) return;

          const rect = overlay.getBoundingClientRect();
          // We want the overlay top to be at -50px relative to the viewport top
          const delta = rect.top + 50; 
          
          if (Math.abs(delta) > 1) {
            window.scrollBy(0, delta);
          }
        } catch (e) {
          // no-op
        }
      };

      if (typeof this.$nextTick === 'function') {
        this.$nextTick(() => {
          // rAF helps ensure layout is up to date on iOS.
          requestAnimationFrame(alignOverlay);
        });
      } else {
        requestAnimationFrame(alignOverlay);
      }
    }, 50);
  },

  closeProductPopup: function () {
    this.productPopupOpen = false;
  },

  // Variations (for variable products)
  variationAttributesList: [],
  selectedVariationAttributes: {},
  selectedVariation: null,

  initVariationState: function () {
    this.variationAttributesList = [];
    this.selectedVariationAttributes = {};
    this.selectedVariation = null;

    if (!this.productData || this.productData.type !== 'variable') {
      return;
    }

    const attrs = this.productData.variation_attributes || {};
    const labels = this.productData.variation_attribute_labels || {};

    this.variationAttributesList = Object.keys(attrs).map((key) => {
      return {
        key,
        label: labels[key] || key,
        options: attrs[key] || [],
      };
    });

    // Default selection based on calculator stage:
    // - Adult => first option
    // - Puppy => second option
    const defaultOptionIndex = (this.formData && this.formData.lifeStage === 'Puppy') ? 1 : 0;

    Object.keys(attrs).forEach((key) => {
      const options = attrs[key] || [];
      const chosen = options[defaultOptionIndex] || options[0] || null;

      this.selectedVariationAttributes[key] = chosen ? (chosen.value || '') : '';
    });

    this.updateSelectedVariation();
  },

  updateSelectedVariation: function () {
    if (!this.productData || this.productData.type !== 'variable') {
      return;
    }

    const attrs = this.selectedVariationAttributes || {};
    const keys = Object.keys(attrs);

    // Wait until all attributes are selected.
    if (keys.some((k) => !attrs[k])) {
      this.selectedVariation = null;
      return;
    }

    const match = (this.productData.variations || []).find((v) => {
      if (v && v.is_in_stock === false) return false;

      const vAttrs = (v && v.attributes) ? v.attributes : {};
      for (const k in vAttrs) {
        // "Any" variations store empty attribute values.
        if (vAttrs[k] === '') continue;
        if ((attrs[k] || '') !== vAttrs[k]) return false;
      }

      return true;
    }) || null;

    // Assign once (avoids flicker from briefly setting selectedVariation to null).
    this.selectedVariation = match;
  },

fetchProductData: function(productId) {
    // Only show the popup once we have the product data (no intermediate loading state).
    this.productPopupOpen = false;
    this.productData = null;

    let formData = new FormData();
    formData.append('action', 'get_product_data');
    formData.append('product_id', productId);

    const minDelay = 2000; // 2 seconds delay
    const start = Date.now();

    fetch(ajaxurl, {
      method: 'POST',
      body: formData
    })
    .then(response => response.json())
    .then(data => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, minDelay - elapsed);

      setTimeout(() => {
        if (data.success) {
          this.productData = data.data;
          this.initVariationState();
          this.openProductPopup();
        } else {
          this.closeProductPopup();
        }
      }, remaining);
    })
    .catch(() => {
      this.closeProductPopup();
    });
  },
  applyCouponToCart: function (couponCode) {
    try {
      const formData = new FormData();
      formData.append('action', 'apply_sample_coupon');
      formData.append('coupon_code', couponCode);

      return fetch(ajaxurl, {
        method: 'POST',
        body: formData,
      })
        .then((res) => res.json())
        .catch(() => null);
    } catch (e) {
      return Promise.resolve(null);
    }
  },

  fetchCartAndDispatch: function (fallbackCart = null) {
    return fetch('/wp-json/cocart/v2/cart')
      .then((r) => r.json())
      .then((cart) => {
        const finalCart = (cart && cart.cart_key) ? cart : fallbackCart;
        if (!finalCart) return;

        const count = finalCart.items ? finalCart.items.length : 0;
        window.dispatchEvent(new CustomEvent('update-count', { detail: { count } }));
        window.dispatchEvent(new CustomEvent('update-cart', { detail: finalCart }));
      })
      .catch(() => {
        if (!fallbackCart) return;
        const count = fallbackCart.items ? fallbackCart.items.length : 0;
        window.dispatchEvent(new CustomEvent('update-count', { detail: { count } }));
        window.dispatchEvent(new CustomEvent('update-cart', { detail: fallbackCart }));
      });
  },

  addToCart: function(productId, variation = null, discountCode = null) {
    let payload = {
      id: `${productId}`,
      quantity: "1",
    };

    if (variation && typeof variation === 'object') {
      payload.variation = variation;
    }

    // Close modal immediately
    this.closeProductPopup();

    fetch("/wp-json/cocart/v2/cart/add-item", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
    .then(response => response.json())
    .then(async (data) => {
      // CoCart returns the full cart object on success, or an error object
      // We check for a property that exists on a successful cart response, like cart_key
      if (data.cart_key) {
        this.productData = null;
        this.selectedVariationAttributes = {};
        this.selectedVariation = null;

        if (discountCode) {
          await this.applyCouponToCart(discountCode);
        }
        await this.fetchCartAndDispatch(data);
      } else {
        alert(data.message || 'Could not add product to cart.');
      }
    })
    .catch(() => {
      alert('An error occurred.');
    });
  },
  maxStep: 1, // control when is the submit button enabled
  currentStep: 1, // control which step is highlighted
  formData: {
    lifeStage: null,
    weight: null,
    ageGroup: null,
  },
  result: {
    lose: null,
    maintain: null,
    gain: null,
    recommended: null,
  },
  openCalculator: function () {
    this.reset();
    this.open = true;
    // prevent scrolling when calculator is open
    const scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    document.body.style.top = `-${scrollY}px`;
  },
  closeCalculator: function () {
    this.open = false;
    // allow scrolling when calculator is closed
    const scrollY = document.body.style.top;
    document.body.style.position = "";
    document.body.style.width = "";
    document.body.style.top = '';
    window.scrollTo(0, parseInt(scrollY || '0') * -1);
  },
  reset: function () {
    this.resetFormData();
    this.resetResults();

    this.maxStep = 1;
    this.currentStep = 1;
  },
  resetFormData: function () {
    this.formData = {
      lifeStage: null,
      weight: null,
      ageGroup: null,
    };
  },
  resetResults: function () {
    this.result = {
      lose: null,
      maintain: null,
      gain: null,
      recommended: null,
    };
  },
  changeStage: function (newStage, maxStep) {
    this.reset();
    this.formData.lifeStage = newStage;
    this.currentStep = 2;
    this.maxStep = maxStep;
    this.$refs.weightInput.focus();
  },
onSubmit: function (type, productId) {
    let { lifeStage, weight, ageGroup } = this.formData;

    switch (type) {
      case "regular":
        if (!lifeStage) {
          lifeStage = 'Adult';
          this.formData.lifeStage = 'Adult';
        }
        // reference: https://southenddogtraining.co.uk/raw-food-feeding-guide/
        if (lifeStage && lifeStage.toLowerCase() === "adult") {
          this.result = {
            ...this.result,
            lose: Math.round(weight * 0.02 * 1000) + "G",
            maintain: Math.round(weight * 0.03 * 1000) + "G",
            gain: Math.round(weight * 0.04 * 1000) + "G",
          };
        } else {
          let recommended;

          const ratio = [
            0.08, // 0-4 months
            0.06, // 4-6 months
            0.04, // 6-12 months
          ];

          this.result = {
            ...this.result,
            recommended:
              Math.round(weight * ratio[ageGroup.index] * 1000) + "G",
          };
        }
        break;
      case "cold_pressed": {
        let recommended;
        // value is bounded for some weights as suggested by cold press guide table(Daily Quantity)
        if (weight < 5) {
          recommended = weight * 24;
        } else if (weight <= 10) {
          recommended = weight * 19;
          recommended = Math.max(Math.min(recommended, 181), 108);
        } else if (weight <= 20) {
          recommended = weight * 17;
          recommended = Math.max(Math.min(recommended, 305), 181);
        } else if (weight <= 30) {
          recommended = weight * 14;
          recommended = Math.max(Math.min(recommended, 413), 305);
        } else if (weight <= 40) {
          recommended = weight * 13;
          recommended = Math.max(Math.min(recommended, 513), 413);
        } else {
          recommended = weight * 12;
        }

        this.result = {
          ...this.result,
          recommended: Math.round(recommended) + "G",
        };
        break;
      }
      default:
        break;
    }
    if (productId) {
        this.fetchProductData(productId);
    }
  },
};
window.DogCalculatorState = DogCalculatorState;